import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];
const companyId = () => Number(localStorage.getItem("companyId") || 0);

const val = (r, ...keys) =>
  keys.map((k) => r?.[k]).find((x) => x !== undefined && x !== null) ?? "";

const safe = (v) =>
  v === null || v === undefined ? "" : typeof v === "object" ? "" : String(v);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const errMsg = (e, fallback) =>
  e?.response?.data?.message ||
  e?.response?.data?.title ||
  e?.message ||
  fallback;

export default function GRNPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [rows, setRows] = useState([]);

  const [loadingMasters, setLoadingMasters] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    grnNo: "",
    grnDate: today(),
    supplierId: "",
    warehouseId: "",
    purchaseOrderId: "",
    purchaseOrderNo: "",
    status: "RECEIVED",
    notes: "",
  });

  const [detail, setDetail] = useState({
    itemId: "",
    qty: 1,
    rate: 0,
    batchNo: "",
    serialNo: "",
    expiryDate: "",
    remarks: "",
  });

  const [details, setDetails] = useState([]);

  const totalQty = useMemo(
    () => details.reduce((s, x) => s + num(x.qty), 0),
    [details],
  );

  const totalValue = useMemo(
    () => details.reduce((s, x) => s + num(x.qty) * num(x.rate), 0),
    [details],
  );

  useEffect(() => {
    loadMasters();
    loadRows();
    loadNextNo();
  }, []);

  const loadMasters = async () => {
    const cid = companyId();

    try {
      setLoadingMasters(true);

      const [s, w, i, po] = await Promise.all([
        client.get("/suppliers", {
          params: cid ? { companyId: cid } : {},
        }),
        client.get("/inventory/warehouses", {
          params: cid ? { companyId: cid } : {},
        }),
        client.get("/inventory/items", {
          params: cid ? { companyId: cid } : {},
        }),
        client.get("/purchase-orders", {
          params: cid ? { companyId: cid } : {},
        }),
      ]);

      setSuppliers(getArray(s.data));
      setWarehouses(getArray(w.data));
      setItems(getArray(i.data));
      setPurchaseOrders(getArray(po.data));
    } catch (e) {
      alert(errMsg(e, "Failed to load masters"));
      setSuppliers([]);
      setWarehouses([]);
      setItems([]);
      setPurchaseOrders([]);
    } finally {
      setLoadingMasters(false);
    }
  };

  const loadNextNo = async () => {
    try {
      const res = await client.get("/grn/next-no", {
        params: { companyId: companyId() || undefined },
      });

      const nextNo = safe(
        val(res.data, "GRNNo", "grnNo", "GrnNo", "grnNumber", "nextNo"),
      );

      setForm((x) => ({
        ...x,
        grnNo: nextNo || x.grnNo || `GRN-${Date.now().toString().slice(-5)}`,
      }));
    } catch {
      setForm((x) => ({
        ...x,
        grnNo: x.grnNo || `GRN-${Date.now().toString().slice(-5)}`,
      }));
    }
  };

  const loadRows = async () => {
    try {
      setLoadingRows(true);

      const res = await client.get("/grn", {
        params: { companyId: companyId() || undefined },
      });

      setRows(getArray(res.data));
    } catch {
      setRows([]);
    } finally {
      setLoadingRows(false);
    }
  };

  const onPOChange = (poId) => {
    const po = purchaseOrders.find(
      (x) => String(val(x, "Id", "id")) === String(poId),
    );

    setForm((prev) => ({
      ...prev,
      purchaseOrderId: poId,
      purchaseOrderNo: safe(
        val(po, "OrderNo", "orderNo", "PurchaseOrderNo", "purchaseOrderNo"),
      ),
      supplierId: val(po, "SupplierId", "supplierId") || prev.supplierId,
    }));
  };

  const onItemChange = (itemId) => {
    const item = items.find(
      (x) => String(val(x, "Id", "id")) === String(itemId),
    );

    setDetail((prev) => ({
      ...prev,
      itemId,
      rate:
        num(
          val(
            item,
            "CostPrice",
            "costPrice",
            "PurchaseRate",
            "purchaseRate",
            "Rate",
            "rate",
          ),
        ) || 0,
    }));
  };

  const addDetail = () => {
    if (!detail.itemId) return alert("Select item");
    if (num(detail.qty) <= 0) return alert("Qty must be greater than zero");
    if (num(detail.rate) < 0) return alert("Invalid rate");

    const item = items.find(
      (x) => String(val(x, "Id", "id")) === String(detail.itemId),
    );

    const itemName = safe(val(item, "ItemName", "itemName", "Name", "name"));
    if (!itemName) return alert("Selected item not found");

    setDetails((prev) => [
      ...prev,
      {
        itemId: Number(detail.itemId),
        itemCode: safe(val(item, "ItemCode", "itemCode", "Code", "code")),
        itemName,
        qty: num(detail.qty),
        rate: num(detail.rate),
        batchNo: safe(detail.batchNo),
        serialNo: safe(detail.serialNo),
        expiryDate: detail.expiryDate || null,
        remarks: safe(detail.remarks),
      },
    ]);

    setDetail({
      itemId: "",
      qty: 1,
      rate: 0,
      batchNo: "",
      serialNo: "",
      expiryDate: "",
      remarks: "",
    });
  };

  const removeDetail = (index) => {
    setDetails((prev) => prev.filter((_, i) => i !== index));
  };

  const save = async () => {
    if (!form.grnNo.trim()) return alert("GRN No is required");
    if (!form.grnDate) return alert("GRN Date is required");
    if (!form.supplierId) return alert("Supplier is required");
    if (!form.warehouseId) return alert("Warehouse is required");
    if (details.length === 0) return alert("Add at least one item");

    try {
      setSaving(true);

      await client.post("/grn", {
        companyId: companyId(),
        grnNo: form.grnNo.trim(),
        grnDate: form.grnDate,
        supplierId: Number(form.supplierId),
        warehouseId: Number(form.warehouseId),
        purchaseOrderId: form.purchaseOrderId
          ? Number(form.purchaseOrderId)
          : null,
        purchaseOrderNo: form.purchaseOrderNo || null,
        status: form.status || "RECEIVED",
        notes: safe(form.notes),
        totalQty,
        totalValue,
        details: details.map((d) => ({
          itemId: Number(d.itemId),
          qty: num(d.qty),
          rate: num(d.rate),
          batchNo: d.batchNo || null,
          serialNo: d.serialNo || null,
          expiryDate: d.expiryDate || null,
          remarks: d.remarks || null,
        })),
      });

      alert("GRN saved successfully. Stock has been increased.");

      setForm({
        grnNo: "",
        grnDate: today(),
        supplierId: "",
        warehouseId: "",
        purchaseOrderId: "",
        purchaseOrderNo: "",
        status: "RECEIVED",
        notes: "",
      });

      setDetails([]);

      await loadNextNo();
      await loadRows();
    } catch (e) {
      alert(errMsg(e, "Failed to save GRN"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-box-arrow-in-down me-2"></i>
            GRN / Goods Receipt Note
          </h6>
          <div style={styles.sub}>
            Receive goods from supplier and increase warehouse stock before
            purchase voucher
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2">
            <Stat title="Items" value={details.length} />
            <Stat title="Total Qty" value={money(totalQty)} />
            <Stat title="Stock Value" value={`₹ ${money(totalValue)}`} />
            <Stat title="Status" value={form.status} />
          </div>

          <div style={styles.section}>GRN Header</div>

          <div className="row g-2 mb-2">
            <Field label="GRN No">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.grnNo}
                onChange={(e) => setForm({ ...form, grnNo: e.target.value })}
              />
            </Field>

            <Field label="GRN Date">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.grnDate}
                onChange={(e) => setForm({ ...form, grnDate: e.target.value })}
              />
            </Field>

            <Field label="Purchase Order" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.purchaseOrderId}
                onChange={(e) => onPOChange(e.target.value)}
                disabled={loadingMasters}
              >
                <option value="">
                  {loadingMasters ? "Loading..." : "Direct GRN / No PO"}
                </option>
                {purchaseOrders.map((po, i) => (
                  <option
                    key={val(po, "Id", "id") || i}
                    value={val(po, "Id", "id")}
                  >
                    {safe(
                      val(
                        po,
                        "OrderNo",
                        "orderNo",
                        "PurchaseOrderNo",
                        "purchaseOrderNo",
                      ),
                    )}{" "}
                    - {safe(val(po, "SupplierName", "supplierName"))}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Supplier" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.supplierId}
                onChange={(e) =>
                  setForm({ ...form, supplierId: e.target.value })
                }
                disabled={loadingMasters}
              >
                <option value="">
                  {loadingMasters ? "Loading..." : "Select Supplier"}
                </option>
                {suppliers.map((s, i) => (
                  <option
                    key={val(s, "Id", "id") || i}
                    value={val(s, "Id", "id")}
                  >
                    {safe(
                      val(s, "SupplierName", "supplierName", "Name", "name"),
                    )}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Warehouse" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.warehouseId}
                onChange={(e) =>
                  setForm({ ...form, warehouseId: e.target.value })
                }
                disabled={loadingMasters}
              >
                <option value="">
                  {loadingMasters ? "Loading..." : "Select Warehouse"}
                </option>
                {warehouses.map((w, i) => (
                  <option
                    key={val(w, "Id", "id") || i}
                    value={val(w, "Id", "id")}
                  >
                    {safe(
                      val(w, "WarehouseCode", "warehouseCode", "Code", "code"),
                    )}{" "}
                    -{" "}
                    {safe(
                      val(w, "WarehouseName", "warehouseName", "Name", "name"),
                    )}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Status">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="RECEIVED">RECEIVED</option>
                <option value="PARTIAL">PARTIAL</option>
                <option value="BILLED">BILLED</option>
                <option value="CANCELLED">CANCELLED</option>
              </select>
            </Field>

            <Field label="Notes" col="col-md-4">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </div>

          <div style={styles.section}>Received Item Details</div>

          <div
            className="row g-2 mb-2 p-2"
            style={{
              background: "#f8fafc",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <Field label="Item" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={detail.itemId}
                onChange={(e) => onItemChange(e.target.value)}
                disabled={loadingMasters}
              >
                <option value="">
                  {loadingMasters ? "Loading..." : "Select Item"}
                </option>
                {items.map((item, i) => (
                  <option
                    key={val(item, "Id", "id") || i}
                    value={val(item, "Id", "id")}
                  >
                    {safe(val(item, "ItemCode", "itemCode", "Code", "code"))} -{" "}
                    {safe(val(item, "ItemName", "itemName", "Name", "name"))}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Qty">
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.qty}
                onChange={(e) => setDetail({ ...detail, qty: e.target.value })}
              />
            </Field>

            <Field label="Rate">
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.rate}
                onChange={(e) => setDetail({ ...detail, rate: e.target.value })}
              />
            </Field>

            <Field label="Batch No">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.batchNo}
                onChange={(e) =>
                  setDetail({ ...detail, batchNo: e.target.value })
                }
              />
            </Field>

            <Field label="Serial No">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.serialNo}
                onChange={(e) =>
                  setDetail({ ...detail, serialNo: e.target.value })
                }
              />
            </Field>

            <Field label="Expiry">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.expiryDate}
                onChange={(e) =>
                  setDetail({ ...detail, expiryDate: e.target.value })
                }
              />
            </Field>

            <Field label="Remarks" col="col-md-2">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.remarks}
                onChange={(e) =>
                  setDetail({ ...detail, remarks: e.target.value })
                }
              />
            </Field>

            <div className="col-md-1 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                style={styles.btn}
                onClick={addDetail}
                disabled={loadingMasters}
              >
                Add
              </button>
            </div>
          </div>

          <div style={styles.tableWrap} className="mb-2">
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Code",
                    "Item",
                    "Qty",
                    "Rate",
                    "Value",
                    "Batch",
                    "Serial",
                    "Expiry",
                    "Remarks",
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        ["Qty", "Rate", "Value"].includes(h) ? "text-end" : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {details.length === 0 ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      No items added
                    </td>
                  </tr>
                ) : (
                  details.map((d, i) => (
                    <tr key={`${d.itemId}-${i}`}>
                      <td style={styles.td}>{safe(d.itemCode) || "-"}</td>
                      <td style={styles.td}>{safe(d.itemName)}</td>
                      <td className="text-end" style={styles.td}>
                        {money(d.qty)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(d.rate)}
                      </td>
                      <td className="text-end fw-bold" style={styles.td}>
                        {money(num(d.qty) * num(d.rate))}
                      </td>
                      <td style={styles.td}>{safe(d.batchNo) || "-"}</td>
                      <td style={styles.td}>{safe(d.serialNo) || "-"}</td>
                      <td style={styles.td}>{d.expiryDate || "-"}</td>
                      <td style={styles.td}>{safe(d.remarks) || "-"}</td>
                      <td style={styles.td}>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          style={{ fontSize: 10 }}
                          onClick={() => removeDetail(i)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-end gap-2 mb-3">
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ ...styles.btn, minWidth: 100 }}
              onClick={() => window.print()}
            >
              Print
            </button>

            <button
              className="btn btn-success btn-sm"
              style={{ ...styles.btn, minWidth: 160 }}
              disabled={saving}
              onClick={save}
            >
              {saving ? "Saving..." : "Save GRN"}
            </button>
          </div>

          <div style={styles.section}>Recent GRNs</div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "GRN No",
                    "Date",
                    "Supplier",
                    "Warehouse",
                    "PO No",
                    "Items",
                    "Qty",
                    "Status",
                  ].map((h) => (
                    <th key={h} style={styles.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      {loadingRows ? "Loading..." : "No GRN records found"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={val(r, "Id", "id") || i}>
                      <td style={{ ...styles.td, fontWeight: 800 }}>
                        {safe(val(r, "GRNNo", "grnNo", "GrnNo"))}
                      </td>
                      <td style={styles.td}>
                        {String(val(r, "GRNDate", "grnDate") || "").slice(
                          0,
                          10,
                        ) || "-"}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "SupplierName", "supplierName")) || "-"}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "WarehouseName", "warehouseName")) || "-"}
                      </td>
                      <td style={styles.td}>
                        {safe(
                          val(
                            r,
                            "PurchaseOrderNo",
                            "purchaseOrderNo",
                            "OrderNo",
                            "orderNo",
                          ),
                        ) || "-"}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "TotalItems", "totalItems")) || "0"}
                      </td>
                      <td style={styles.td}>
                        {money(val(r, "TotalQty", "totalQty"))}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "Status", "status")) || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { fontSize: 11, padding: 0 },
  card: {
    background: "#fff",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
  },
  header: {
    background: "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
    color: "#fff",
    padding: "9px 12px",
  },
  title: { fontSize: 13, fontWeight: 800, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3 },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  section: {
    fontSize: 11,
    fontWeight: 800,
    color: "#0f766e",
    borderBottom: "1px solid #e9ecef",
    paddingBottom: 4,
    margin: "8px 0 6px",
  },
  tableWrap: {
    maxHeight: 390,
    overflow: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 1350, marginBottom: 0 },
  th: {
    fontSize: 10,
    color: "#fff",
    background: "#07111f",
    padding: "7px 8px",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  td: { fontSize: 11, padding: "6px 8px", whiteSpace: "nowrap" },
};

function Field({ label, children, col = "col-md-2" }) {
  return (
    <div className={col}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-md-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 800 }}>
          {title}
        </div>
        <div style={{ fontSize: 15, color: "#0f766e", fontWeight: 900 }}>
          {value}
        </div>
      </div>
    </div>
  );
}
